import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaUser, FaLock } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import { UserLoginCheck, changePassword } from "../../components/Api/LoginApiService";
import { ClipLoader } from "react-spinners";
import "./Login.css";
import CompanyBranchModal from "../../components/Common/CompanyBranchModal";
import zenloomerIcon from "../../assets/images/zenloomer-icon.png";
import zenloomerHorizontal from "../../assets/images/zenloomer-horizontal.png";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    username: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showChangePasswordFields, setShowChangePasswordFields] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordSuccess, setChangePasswordSuccess] = useState("");
  const [showCompanyBranchModal, setShowCompanyBranchModal] = useState(false);
  const [pendingAuthResponse, setPendingAuthResponse] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await UserLoginCheck({ username, password });

      if (response && response.token) {
        // Store auth data
        const authData = {
          accessToken: response.token,
          refreshToken: response.refreshToken,
          userId: response.userId || response.user_Id,
          userName: response.userName,
          employeeId: response.employeeId,
          branchId: response.branchId,
          companyId: response.companyId,
          isAdmin: response.isAdmin,
          currencyCode: response.currencyCode,
          isAuthenticated: true
        };

        localStorage.setItem("authUser", JSON.stringify(authData));
        localStorage.setItem("token", response.token);
        localStorage.setItem("currencyCode", response.currencyCode || "AED");
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("userId", response.userId || response.user_Id);

        // Store user's branch and company IDs from login response (always update, even if null)
        // This ensures stale values are cleared when login response has null
        const branchId = response.branchId || response.user?.user_Branch_Id;
        const companyId = response.companyId || response.user?.user_Company_Id;

        // Always set/clear these values based on login response
        if (branchId !== null && branchId !== undefined) {
          localStorage.setItem("userBranchId", branchId.toString());
        } else {
          localStorage.removeItem("userBranchId");
        }
        if (companyId !== null && companyId !== undefined) {
          localStorage.setItem("userCompanyId", companyId.toString());
        } else {
          localStorage.removeItem("userCompanyId");
        }

        if (response.user) {
          localStorage.setItem("user", JSON.stringify(response.user));
        }

        // Check if we need to show the company/branch selection modal
        const needsSelection = response.isAdmin || (!response.isAdmin && (!response.companyId || !response.branchId));
        
        if (needsSelection) {
          setPendingAuthResponse(response);
          setShowCompanyBranchModal(true);
        } else {
          navigate("/Dashboard");
        }
      } else {
        setError("Invalid username or password");
      }
    } catch (error) {
      // Display the actual error message from the API
      const errorMessage = error.message || "Invalid username or password. Please try again.";
      setError(errorMessage);
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangePasswordError("");
    setChangePasswordSuccess("");
    setChangePasswordLoading(true);

    // Validation
    if (!changePasswordData.username.trim()) {
      setChangePasswordError("Username is required");
      setChangePasswordLoading(false);
      return;
    }

    if (!changePasswordData.currentPassword.trim()) {
      setChangePasswordError("Current password is required");
      setChangePasswordLoading(false);
      return;
    }

    if (!changePasswordData.newPassword.trim()) {
      setChangePasswordError("New password is required");
      setChangePasswordLoading(false);
      return;
    }

    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      setChangePasswordError("New password and confirm password do not match");
      setChangePasswordLoading(false);
      return;
    }

    try {
      const response = await changePassword({
        username: changePasswordData.username,
        currentPassword: changePasswordData.currentPassword,
        newPassword: changePasswordData.newPassword
      });

      if (response.success) {
        setChangePasswordSuccess("Password changed successfully!");
        setChangePasswordData({
          username: "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        });

        // Close modal after 2 seconds
        setTimeout(() => {
          setShowChangePasswordModal(false);
          setChangePasswordSuccess("");
        }, 2000);
      }
    } catch (error) {
      setChangePasswordError(error.message);
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const handleChangePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setChangePasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleChangePasswordField = (field) => {
    setShowChangePasswordFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const openChangePasswordModal = () => {
    setShowChangePasswordModal(true);
    setChangePasswordError("");
    setChangePasswordSuccess("");
    setChangePasswordData({
      username: "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
  };

  const closeChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setChangePasswordError("");
    setChangePasswordSuccess("");
    setChangePasswordData({
      username: "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
  };

  const handleSelectionComplete = (company, branch) => {
    // Update authUser in localStorage
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    authUser.companyId = company.business_Id;
    authUser.branchId = branch.branch_Id;
    localStorage.setItem("authUser", JSON.stringify(authUser));

    // Update individual storage items
    localStorage.setItem("userCompanyId", company.business_Id.toString());
    localStorage.setItem("userBranchId", branch.branch_Id.toString());

    // Update user object if it exists
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (Object.keys(user).length > 0) {
      user.user_Company_Id = company.business_Id;
      user.user_Branch_Id = branch.branch_Id;
      localStorage.setItem("user", JSON.stringify(user));
    }

    setShowCompanyBranchModal(false);
    navigate("/Dashboard");
  };

  const handleModalCancel = () => {
    setShowCompanyBranchModal(false);
    navigate("/Dashboard");
  };

  return (
    <>
      <div className="login-container min-vh-100 d-flex align-items-center justify-content-center">
        <div className="login-background"></div>
        <div className="container position-relative">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="login-card">
                <div className="login-card-header">
                  <div className="login-logo-wrapper">
                    <div className="login-icon-circle">
                      <img src={zenloomerIcon} className="login-logo-img" alt="Logo" style={{ width: '45px', height: '45px', objectFit: 'contain' }} />
                    </div>
                  </div>
                  <div className="login-brand-logo-container">
                    {/* <img src={zenloomerHorizontal} alt="Zenloomer" className="login-brand-logo" /> */}
                    <span className="login-brand-text">Zenloomer</span>
                  </div>
                  <p className="login-subtitle">Welcome Back!</p>
                </div>
                <div className="login-card-body">
                  {error && (
                    <div className="login-alert-error">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleLogin} className="login-form">
                    <div className="login-input-wrapper">
                      <div className="login-input-group">
                        <span className="login-input-icon">
                          <FaUser />
                        </span>
                        <input
                          type="text"
                          className="login-input-field"
                          placeholder="Username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="login-input-wrapper">
                      <div className="login-input-group">
                        <span className="login-input-icon">
                          <FaLock />
                        </span>
                        <input
                          type={showPassword ? "text" : "password"}
                          className="login-input-field"
                          placeholder="Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="login-password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>

                    <div className="login-button-wrapper">
                      <button
                        type="submit"
                        className="login-submit-btn"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <ClipLoader size={18} color="#ffffff" className="me-2" />
                            Signing In...
                          </>
                        ) : (
                          <>
                            <span>Sign In</span>
                            <FaLock className="ms-2" size={14} />
                          </>
                        )}
                      </button>
                    </div>
                  </form>

                  <div className="login-footer">
                    <button
                      type="button"
                      className="login-link-btn"
                      onClick={openChangePasswordModal}
                    >
                      Change Password
                    </button>
                    <div className="login-company-credit">
                      Powered by <a href="https://byteloomer.com/" target="_blank" rel="noopener noreferrer">Byteloomer Technologies Pvt. Ltd.</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeChangePasswordModal}
                ></button>
              </div>
              <div className="modal-body">
                {changePasswordError && (
                  <div className="alert alert-danger py-2">
                    {changePasswordError}
                  </div>
                )}
                {changePasswordSuccess && (
                  <div className="alert alert-success py-2">
                    {changePasswordSuccess}
                  </div>
                )}

                <form onSubmit={handleChangePassword}>
                  <div className="mb-3">
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      name="username"
                      value={changePasswordData.username}
                      onChange={handleChangePasswordInputChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Current Password</label>
                    <div className="input-group">
                      <input
                        type={showChangePasswordFields.currentPassword ? "text" : "password"}
                        className="form-control"
                        name="currentPassword"
                        value={changePasswordData.currentPassword}
                        onChange={handleChangePasswordInputChange}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-light border"
                        onClick={() => toggleChangePasswordField('currentPassword')}
                      >
                        {showChangePasswordFields.currentPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">New Password</label>
                    <div className="input-group">
                      <input
                        type={showChangePasswordFields.newPassword ? "text" : "password"}
                        className="form-control"
                        name="newPassword"
                        value={changePasswordData.newPassword}
                        onChange={handleChangePasswordInputChange}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-light border"
                        onClick={() => toggleChangePasswordField('newPassword')}
                      >
                        {showChangePasswordFields.newPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Confirm New Password</label>
                    <div className="input-group">
                      <input
                        type={showChangePasswordFields.confirmPassword ? "text" : "password"}
                        className="form-control"
                        name="confirmPassword"
                        value={changePasswordData.confirmPassword}
                        onChange={handleChangePasswordInputChange}
                        required
                      />
                      <button
                        type="button"
                        className="btn btn-light border"
                        onClick={() => toggleChangePasswordField('confirmPassword')}
                      >
                        {showChangePasswordFields.confirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeChangePasswordModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={changePasswordLoading}
                    >
                      {changePasswordLoading ? (
                        <>
                          <ClipLoader size={16} color="#ffffff" className="me-2" />
                          Changing...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showChangePasswordModal && (
        <div className="modal-backdrop fade show"></div>
      )}

      {/* Company & Branch Selection Modal */}
      <CompanyBranchModal
        isOpen={showCompanyBranchModal}
        isAdmin={pendingAuthResponse?.isAdmin}
        onSelectionComplete={handleSelectionComplete}
        onCancel={handleModalCancel}
      />
    </>
  );
};

export default Login;
